import axios, { AxiosError, AxiosInstance, isAxiosError } from "axios";
import { Dispatch } from "@reduxjs/toolkit";

// Services
import { api } from "src/services/api";

// Models & Components
import { callDataLoaded, callEnded } from "./reducer";
import { ReturnData } from "src/types/return-data";

export enum RefusalType {
  ACTIVE = "ativa",
  PASSIVE = "passiva",
}

let fefeInstance: AxiosInstance;

export const createFefeInstance = (baseURL: string) => {
  if (fefeInstance) return fefeInstance;

  if (!baseURL) throw "Forneça um baseURL para a instancia fefe";

  return (fefeInstance = axios.create({
    baseURL,
    headers: {
      maxBodyLength: Infinity,
      "Content-Type": "multipart/form-data",
      authorization: "393fc31a-2088-41d1-81ed-96e350729ca5",
    },
  }));
};

// ====> Ajax

// Dialer
/**
 * 
 * @param aderente 
 * @param usoInterno 
 {
  aderente_nome: null,
  em_uso: true,
  exclusivo: false,
  id: "d31b513c-7554-42e0-9269-d17654553b67",
  is_active: true,
  password: "Icom#2020",
  ramal: "1011",
  serverDomain: "amesp.ddns.net"
 }
 */
export const doGetRamalRequest = async (aderente?: string, usoInterno: boolean = false) => {
  const defaultErrorMessage = "Não foi possível alocar um ramal. Tente novamente.";
  try {
    // const params = new URLSearchParams();
    const { data, status } = await api.get(`/api/softphone/ramal/`, {
      params: {
        origem: "web",
        ...(aderente ? { aderente } : {}),
      },
    });

    if (status == 200) {
      return {
        success: true,
        data: data.data,
      };
    }

    return {
      success: false,
      message: defaultErrorMessage,
    };
  } catch (error) {
    let message;

    // if (isAxiosError<{ error?: { message: string } }>(error)) {
    //   message = error.response?.data.error?.message;
    // }

    return {
      success: false,
      message: message || defaultErrorMessage,
    };
  }
};

export const doLeaveRamalRequest = async (ramal: string) => {
  const defaultErrorMessage = "Falha ao desalocar ramal";

  if (!ramal)
    return {
      success: false,
      message: "Não foi enviado um ramal para desalocação.",
    };

  try {
    const { status, data } = await api.delete(`/api/softphone/ramal/${ramal}`);

    if (status === 200) {
      return {
        success: true,
      };
    }

    return {
      success: false,
      message: defaultErrorMessage,
    };
  } catch (error) {
    let message;

    if (isAxiosError(error)) {
      message = error.response?.data?.detail || defaultErrorMessage;
    }

    return {
      success: false,
      message: message || error,
    };
  }
};

export const logExternalCall = async (
  aderenteSlug: string,
  options: {
    tipo_ligacao: string;
    numero_externo?: string;
    numero_efetivo?: string;
    chamada_interna?: string;
    ramal_atendente?: string;
    ramal_chamada?: string;
    inicio_chamada?: string;
    status?: string;
    protocolo?: string;
  },
) => {
  const defaultErrorMessage = "Não foi possível registrar a chamada externa. Tente novamente.";

  try {
    const { data, status } = await api.post(`/api/softphone/${aderenteSlug}/atendimento/ligacao-externa/`, options);

    if (status == 201) {
      return {
        success: true,
        data: data,
      };
    }

    return {
      success: false,
      message: defaultErrorMessage,
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: err.message || defaultErrorMessage,
    };
  }
};

export const updateExternalCall = async (aderenteSlug: string, externalCallId: string, options: any = {}) => {
  const defaultErrorMessage = "Não foi possível atualizar a chamada externa. Tente novamente.";

  try {
    const { data, status } = await api.put(`/api/softphone/${aderenteSlug}/atendimento/ligacao-externa/${externalCallId}/`, options);

    if (status == 204) {
      return {
        success: true,
        data: { id: externalCallId, ...data },
      };
    }

    return {
      success: false,
      message: defaultErrorMessage,
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: err.message || defaultErrorMessage,
    };
  }
};

export const finishExternalCall = async (aderenteSlug: string, externalCallId: string) => {
  const defaultErrorMessage = "Não foi possível registrar o fim da chamada externa. Tente novamente.";

  try {
    const { status } = await api.delete(`/api/softphone/${aderenteSlug}/atendimento/ligacao-externa/${externalCallId}/`);

    return status === 204 ? { success: true } : { success: false };
  } catch (error) {
    const err = error as AxiosError;

    return { success: false, message: err.message || defaultErrorMessage };
  }
};

export const getExternalCalls = async (aderenteSlug: string, callId: string): Promise<ReturnData> => {
  const defaultErrorMessage = "Não foi possível buscar as chamadas externas da chamada.";

  try {
    const { status, data } = await api.get(`/api/softphone/${aderenteSlug}/atendimento/ligacao-externa/${callId}/`);

    return status === 200 ? { success: true, data: data } : { success: false };
  } catch (error) {
    const err = error as AxiosError;

    return { success: false, message: err.message || defaultErrorMessage };
  }
};

// Fefe methods
export const doUploadRecording = async (
  callId: string,
  token: string,
  aderenteSlug: string,
  udaId: string,
  serviceId: string,
  data?: FormData,
  step = "upload",
  params?: any,
): Promise<ReturnData> => {
  try {
    const { status } = await fefeInstance.request({
      url: `/v3/uploadAttendanceFile?callId=${callId}&apiToken=${token}&aderente=${aderenteSlug}&udaId=${udaId}&servico=${serviceId}&fileExt=webm&step=${step}`,
      method: "post",
      params,
      headers: {
        maxBodyLength: Infinity,
        "Content-Type": "multipart/form-data",
        authorization: "393fc31a-2088-41d1-81ed-96e350729ca5",
      },
      data,
    });

    if (status === 200) return { success: true };

    return { success: false, message: "Falha ao enviar video." };
  } catch (ex) {
    return { success: false, message: "Falha ao enviar video." };
  }
};

// Call conference methods
export const getCallDetails = async (callId: string): Promise<ReturnData> => {
  try {
    const [callDetailsResponse] = await Promise.all([api.get(`/api/softphone/atendimento/chamada/${callId}/`)]);
    const { data: callRequestData, status: callRequestStatus } = callDetailsResponse;

    if (callRequestStatus == 200) {
      return {
        success: true,
        data: {
          ...callRequestData,
        },
      };
    }

    return {
      success: false,
      message: "Falha ao buscar dados da chamada",
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: `Falha ao buscar dados da chamada: ${err.message}`,
    };
  }
};

export const getStatusFinalizadorByAderente = async (aderenteSlug: string) => {
  try {
    const { data, status } = await api.get(`/api/softphone/${aderenteSlug}/status-finalizador/`);

    if (status == 200) {
      return {
        success: true,
        data: data.data,
      };
    }

    return {
      success: false,
      message: "Falha ao buscar status finalizadores da aderente",
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: `Falha ao buscar status finalizadores da aderente: ${err.message}`,
    };
  }
};

export const getCallServicesByAderente = async (aderenteSlug: string) => {
  try {
    const { data, status } = await api.get(`/api/softphone/${aderenteSlug}/servicos/`);

    if (status == 200) {
      return {
        success: true,
        data,
      };
    }

    return {
      success: false,
      message: "Falha ao buscar serviços da chamada",
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: `Falha ao buscar serviços da chamada: ${err.message}`,
    };
  }
};

export const createCallConference = async (
  udaId: string,
  ramal: string,
  serviceId: string,
  aderenteSlug: string,
  callType: string,
  origemChamada?: string,
  id_pai?: string,
  id_origem?: string,
) => {
  const defaultErrorMessage = "Falha ao criar nova chamada chamada.";
  try {
    const { status, data } = await api.post(`/api/softphone/${aderenteSlug}/atendimento/chamada/`, {
      ramal_atendente: ramal,
      uda: udaId,
      versao_softphone: import.meta.env.PACKAGE_VERSION,
      servico: serviceId,
      tipo_canal: callType,
      origem_chamada: origemChamada,
      id_pai,
      id_origem,
    });

    if (status === 201) return { success: true, data };

    return { success: false, message: defaultErrorMessage };
  } catch (ex) {
    return { success: false, message: defaultErrorMessage };
  }
};

export const updateCallConference = async (callId: string, options: object): Promise<ReturnData> => {
  const defaultErrorMessage = "Falha ao atualizar dados da chamada.";
  try {
    const { status } = await api.put(`/api/softphone/atendimento/chamada/${callId}/`, options);

    const newCallDataResponse = await getCallDetails(callId);

    if (status === 200 && newCallDataResponse.success) return { success: true, data: newCallDataResponse.data };

    return { success: false, message: defaultErrorMessage };
  } catch (ex) {
    return { success: false, message: defaultErrorMessage };
  }
};

export const startCallConference = async (
  callId: string,
  aderenteSlug: string,
  params: {
    version: string;
    ramal: string;
  },
): Promise<ReturnData> => {
  try {
    const { data: initRequestData, status } = await api.post(`/api/softphone/${aderenteSlug}/atendimento/chamada/${callId}/conferencia/`, {
      versao_softphone: params.version,
      ramal_atendente: params.ramal,
    });

    if (status == 200) {
      return { success: true, data: initRequestData };
    }

    return {
      success: false,
      message: "Falha ao iniciar chamada",
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: err.message || "Falha ao iniciar chamada.",
    };
  }
};

export const finishCallConference = async (callId: string, aderenteSlug: string): Promise<ReturnData> => {
  try {
    const { data: finishRequestData, status } = await api.delete(`/api/softphone/${aderenteSlug}/atendimento/chamada/${callId}/conferencia/`);

    if (status == 200) {
      return { success: true, data: finishRequestData };
    }

    return {
      success: false,
      message: "Falha ao finalizar chamada",
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: `Falha ao finalizar chamada: ${err.message}`,
    };
  }
};

export const sendCallRequestResponse = async ({
  aderenteSlug,
  udaId,
  ramal,
  serviceId,
  callId,
  paQueueId,
  twilioUdaToken,
  twilioRoomName,
}: {
  callId: string;
  aderenteSlug: string;
  udaId: string;
  ramal: string;
  serviceId: string;
  paQueueId: string;
  twilioUdaToken: string;
  twilioRoomName: string;
}) => {
  const defaultErrorMessage = "Falha ao criar nova chamada chamada.";
  try {
    const { status, data } = await api.post(`/api/softphone/${aderenteSlug}/atendimento/requisicao-chamada/`, {
      id_chamada: callId,
      ramal_atendente: ramal,
      uda: udaId,
      paQueueId: paQueueId,
      servico: serviceId,
      twilio_uda_token: twilioUdaToken,
      twilio_room_name: twilioRoomName,
    });

    if (status === 201) return { success: true, data };

    return { success: false, message: defaultErrorMessage };
  } catch (error) {
    const err = error as AxiosError;
    return {
      success: false,
      message: `Falha ao enviar solicitação de reversa chamada: ${err.message}`,
    };
  }
};

export const getUdaDetailsById = async (udaId: string, aderenteSlug: string): Promise<ReturnData> => {
  try {
    let { data } = await api.get(`/api/softphone/${aderenteSlug}/uda/${udaId}/`);

    return { data, success: true };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: `Falha ao buscar dados do UDA: ${err.message}`,
    };
  }
};

export const getServicesByAderente = async (aderenteSlug: string): Promise<ReturnData> => {
  try {
    let { data } = await api.get(`/api/softphone/${aderenteSlug}/servicos/`);

    return { data, success: true };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: `Falha ao buscar dados do serviços: ${err.message}`,
    };
  }
};

// Call Status
export const doStartRigging = (eventData: any) => async (dispatch: Dispatch) => {
  try {
    const { data } = await getCallDetails(eventData.callId!);
    const newCall = {
      ...data,
      pstnNumbers: [...eventData.pstnNumbers],
    };

    const finishStatuses = await getStatusFinalizadorByAderente(data.aderente_slug);

    newCall.finishStatuses = finishStatuses.data;

    dispatch(
      callDataLoaded({
        call: newCall,
        incomingCallId: eventData.callId,
      }),
    );
  } catch (error) {
    dispatch(callEnded());
  }
};

export const markCallRefused = async ({ type, aderenteSlug, callId, idUda }: { type: RefusalType; aderenteSlug: string; callId: string; idUda: string }): Promise<ReturnData> => {
  const defaultErrorMessage = "Falha ao recusar chamada";
  try {
    const { data: finishRequestData, status } = await api.post(`/api/softphone/${aderenteSlug}/atendimento/recusar-chamada/`, { id_uda: idUda, id_chamada: callId, tipo_recusa: type });

    if (status == 200) {
      return { success: true, data: finishRequestData };
    }

    return {
      success: false,
      message: defaultErrorMessage,
    };
  } catch (error) {
    const err = error as AxiosError;

    return {
      success: false,
      message: err.message || defaultErrorMessage,
    };
  }
};
